import { Handle, Position } from "@xyflow/react";
import { Zap, GitBranch, Brain, Crosshair } from "lucide-react";
import { kindMeta } from "./sdkStore";

const ICONS = { trigger: Zap, logic: GitBranch, profile: Brain, action: Crosshair };

function summary(type, d) {
  if (type === "trigger") {
    if (d.kind === "area_arrival") return `zone: ${d.zone || "—"}`;
    if (d.kind === "shots_fired") return `threshold: ${d.threshold ?? 1} rounds`;
    if (d.kind === "call_911") return d.priority || "Priority 1";
    if (d.kind === "time_elapsed") return `t + ${d.seconds ?? 60}s`;
  }
  if (type === "logic") {
    if (d.kind === "escalation_chance") return `${d.chance ?? 50}% escalate`;
    if (d.kind === "mood_check") return `if mood = ${d.mood || "hostile"}`;
    if (d.kind === "sightline_check") return `range ${d.range ?? 30}m`;
  }
  if (type === "profile") return `${d.mood || "agitated"} · comply ${d.compliance ?? 50}% · resist x${d.negotiation_resistance ?? 1}`;
  if (type === "action") {
    if (d.kind === "spawn_swat") return `${d.units ?? 4} units @ ${d.staging || "staging"}`;
    if (d.kind === "trigger_barricade") return d.location || "—";
    if (d.kind === "radio_broadcast") return (d.message || "").slice(0, 34);
    if (d.kind === "set_hostage_state") return `state: ${d.state || "captive"}`;
  }
  return "";
}

function BaseNode({ id, type, data, selected, branchy }) {
  const { cat } = kindMeta(type, data.kind);
  const color = cat?.color || "#2E6BFF";
  const Icon = ICONS[type] || Zap;
  return (
    <div
      data-testid={`node-${type}-${id}`}
      className={`min-w-[190px] max-w-[230px] border bg-[#0a0a0a] shadow-lg transition-colors ${selected ? "border-zinc-100" : "border-white/15"}`}
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-zinc-400 !border !border-black" />
      <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10" style={{ background: `${color}14` }}>
        <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
        <p className="font-mono text-[9px] tracking-[0.15em] uppercase text-zinc-200 truncate">{data.label || data.kind}</p>
      </div>
      <p className="px-3 py-2 font-mono text-[9px] text-zinc-500 truncate">{summary(type, data)}</p>
      {branchy ? (
        <>
          <Handle type="source" id="yes" position={Position.Bottom} style={{ left: "30%" }} className="!w-2.5 !h-2.5 !bg-nc-green !border !border-black" />
          <Handle type="source" id="no" position={Position.Bottom} style={{ left: "70%" }} className="!w-2.5 !h-2.5 !bg-red-500 !border !border-black" />
          <div className="flex justify-between px-6 pb-1.5 -mt-1">
            <span className="font-mono text-[7px] text-nc-green uppercase">yes</span>
            <span className="font-mono text-[7px] text-red-400 uppercase">no</span>
          </div>
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-zinc-400 !border !border-black" />
      )}
    </div>
  );
}

export const TriggerNode = (p) => <BaseNode {...p} type="trigger" />;
export const LogicNode = (p) => <BaseNode {...p} type="logic" branchy />;
export const ProfileNode = (p) => <BaseNode {...p} type="profile" />;
export const ActionNode = (p) => <BaseNode {...p} type="action" />;

export const nodeTypes = { trigger: TriggerNode, logic: LogicNode, profile: ProfileNode, action: ActionNode };
